import axios from 'axios';

// Obtener la URL base de la API
const getBaseURL = () => {
  if (typeof window !== 'undefined') {
    // En el navegador, usar process.env.BASE_URL o detectar según hostname
    return process.env.BASE_URL || 
           (window.location.hostname === 'localhost' 
             ? 'http://localhost:8001/api' 
             : 'https://dashboard.dondelanegra.cl/api');
  }
  // En SSR, usar la variable de entorno
  return process.env.BASE_URL || 'http://localhost:8001/api';
};

const API_URL = getBaseURL();

// Crear instancia de axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Interceptor para agregar token automáticamente
api.interceptors.request.use(
  (config) => {
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      // Token expirado o inválido
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.removeItem('user_login');
      // Redirigir a login si es necesario
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ==========================================================================
// MercadoPago Checkout API (pago directo con tarjeta tokenizada, sin redirección)
// ==========================================================================

// Obtener la public key para inicializar el SDK de MercadoPago
export const getMercadoPagoPublicKey = async () => {
  try {
    const response = await api.get('/mercadopago/public-key');
    return response.data?.public_key;
  } catch (error) {
    throw error;
  }
};

// Obtener las cuotas disponibles según el BIN de la tarjeta y el monto
export const getMercadoPagoInstallments = async (bin, amount) => {
  try {
    const response = await api.get('/mercadopago/installments', {
      params: { bin, amount },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Obtener los bancos emisores según el payment_method_id y el BIN
export const getMercadoPagoCardIssuers = async (paymentMethodId, bin) => {
  try {
    const response = await api.get(`/mercadopago/card-issuers/${paymentMethodId}`, {
      params: { bin },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Procesar el pago directo con la tarjeta tokenizada
export const processMercadoPagoDirectPayment = async (payload) => {
  try {
    const response = await api.post('/mercadopago/process-direct-payment', payload);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Opciones de envío según el descuento por usuario (retiro en tienda / sin envío gratis).
/**
 * Estado completo del descuento del usuario (task-1488).
 *
 * Además del descuento en sí, devuelve el estado calculado por el backend para HOY:
 *  - is_currently_blocked: cae en un rango de fechas bloqueadas (ej. Cyberday).
 *  - monthly_limit_exceeded: ya alcanzó su tope mensual.
 *  - discount_applicable: resumen de si el descuento se puede aplicar hoy.
 *  - pickup_only / block_billing / allow_purchase_without_discount: restricciones.
 *
 * Devuelve null si no hay descuento o si la petición falla (el checkout sigue
 * funcionando sin descuento en vez de romperse).
 */
export const getUserDiscountStatus = async (email) => {
  if (!email) {
    return null;
  }
  try {
    const response = await api.get(
      `/user-discount/check/${encodeURIComponent(email)}`
    );
    return response.data?.data || null;
  } catch (error) {
    return null;
  }
};

/**
 * ¿El usuario puede comprar con factura? (task-1488)
 *
 * El bloqueo depende de si compra CON descuento, por eso se envía applyDiscount.
 * Ante cualquier fallo se permite facturar: el backend vuelve a validarlo al crear
 * la orden, así que un error de red no debe esconder la opción sin motivo.
 */
export const getBillingAvailability = async (applyDiscount = true) => {
  const fallback = { billing_allowed: true, reason: null, blocked_by_discount: false };
  try {
    const response = await api.get('/billing/availability', {
      params: { apply_user_discount: applyDiscount ? 1 : 0 },
    });
    return {
      billing_allowed: response.data?.billing_allowed !== false,
      reason: response.data?.reason ?? null,
      blocked_by_discount: !!response.data?.blocked_by_discount,
    };
  } catch (error) {
    return fallback;
  }
};

// Devuelve el objeto data del backend, o un default seguro si la petición falla.
export const getUserDiscountShippingOptions = async (email) => {
  const fallback = {
    has_active_discount: false,
    pickup_enabled: false,
    no_free_shipping: false,
    pickup_only: false,
    block_billing: false,
    is_currently_blocked: false,
    discount_applicable: false,
  };
  if (!email) {
    return fallback;
  }
  try {
    const response = await api.get(
      `/user-discount/shipping-options/${encodeURIComponent(email)}`
    );
    return response.data?.data || fallback;
  } catch (error) {
    return fallback;
  }
};

export default api;

